import React, { useState } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
	Typography,
	Button,
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	makeStyles,
	Paper,
	TextField,
	Grid,
} from '@material-ui/core';
import { format } from "date-fns";
import fileDownload from "js-file-download";
import { PrintOutlined } from "@material-ui/icons";
import DatePickerACCB from "../Components/DatePickerACCB";
import instance from "api/globalaxios";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import { displayEmail } from "../utils";
import FormError from "../Components/FormError";

const useStyles = makeStyles((theme) => ({
	formWrapper: {
		display: "flex",
		justifyContent: "center",
		padding: "1rem 0 1.5rem",
	},
	formCard: {
		backgroundColor: "white",
		padding: "2rem",
		borderRadius: "0.625rem",
		width: "100%",
		maxWidth: 480,
		display: "flex",
		flexDirection: "column",
		gap: "1.5rem",
		alignItems: "center",
		boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
	},
	submitBtn: {
		// borderRadius: "0.75rem",
		padding: "0.6rem 3rem",
		fontSize: "1rem",
	},
	roomHeader: {
		fontWeight: 600,
		fontSize: "0.9375rem",
		padding: "10px 12px",
		backgroundColor: "#f0f0f0",
		borderTop: "2px solid #ddd",
	},
	tableHeadRow: {
		backgroundColor: "#f5f5f5",
		"& th": {
			padding: "10px",
			fontWeight: 600,
			fontSize: "0.875rem",
		},
	},
	tableBodyCell: {
		padding: "10px",
		fontSize: "0.875rem",
		verticalAlign: "top",
	},
	tableWrapper: {
		maxWidth: "100%",
		padding: "0 12px",
		marginBottom: "1.5rem",
		overflowX: "auto",
	},
	counselLabel: {
		fontWeight: 600,
		fontSize: "0.8125rem",
		marginBottom: 4,
		color: TEXT.primary,
	},
	counselEntry: {
		fontSize: "0.8125rem",
		lineHeight: 1.6,
		color: TEXT.secondary,
		marginBottom: 6,
	},
	counselGroup: {
		marginBottom: 10,
	},
	arbitratorLine: {
		fontSize: "0.875rem",
		lineHeight: 1.5,
		color: TEXT.primary,
	},
}));

const CounselGroup = ({ label, counsels, classes }) => (
	<Box className={classes.counselGroup}>
		<Typography className={classes.counselLabel}>{label}</Typography>
		{!counsels || counsels.length === 0 ? (
			<Typography className={classes.counselEntry}>—</Typography>
		) : (
			counsels.map((c, i) => (
				<Box key={i} style={{ marginBottom: 6 }}>
					<Typography className={classes.counselEntry} style={{ marginBottom: 0 }}>
						{c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
					</Typography>
					{c.email && (
						<Typography className={classes.counselEntry} style={{ marginBottom: 0 }}>
							{displayEmail(c.email)}
						</Typography>
					)}
					{c.phone_number && (
						<Typography className={classes.counselEntry} style={{ marginBottom: 0 }}>
							{c.phone_number}
						</Typography>
					)}
				</Box>
			))
		)}
	</Box>
);

// ── component ────────────────────────────────────────────────────────────────

const CauseList = () => {
	const classes = useStyles();
	const [date, setDate] = useState(null);
	const [causeListType, setCauseListType] = useState("main");
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [fetchedDate, setFetchedDate] = useState(null);
	const [downloading, setDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState("");
	const [fetchError, setFetchError] = useState("");

	const handleDownload = () => {
		setDownloadError("");
		setDownloading(true);
		const params = { cause_list_report: false, cause_list_type: causeListType };
		if (date) params.listing_date = format(date, "yyyy-MM-dd");
		instance
			.get("/bac/reports/causelist/generate/", { params, responseType: "blob" })
			.then((res) => {
				fileDownload(res.data, "Cause List Report.pdf");
				setDownloading(false);
			})
			.catch((err) => {
				console.error("Error downloading cause list PDF:", err);
				setDownloadError("Failed to download PDF. Please try again.");
				setDownloading(false);
			});
	};

	const handleSubmit = () => {
		if (!date) return;
		const formatted = format(date, "yyyy-MM-dd");
		setLoading(true);
		setFetchError("");
		setData(null);
		instance
			.get(`/bac/causlist/`, { params: { listing_date: formatted, cause_list_type: causeListType } })
			.then((res) => {
				setData(res?.data?.data || {});
				setFetchedDate(format(date, "dd/MM/yyyy"));
				setLoading(false);
			})
			.catch((err) => {
				console.error("Error fetching cause list:", err);
				setFetchError("Failed to fetch cause list for the selected date.");
				setLoading(false);
			});
	};

	const rooms = data ? Object.keys(data) : [];

	// global row counter across all rooms
	let globalCounter = 0;

	return (
		<Box>

			{/* Date picker */}
			<Box className={classes.formWrapper}>
				<Box className={classes.formCard}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<Box>
								<Typography
									style={{ color: TEXT.strong, marginBottom: 6, fontSize: '0.83rem', textAlign: 'left' }}
								>
									Select Date
								</Typography>
								<DatePickerACCB value={date} onChange={setDate} />
							</Box>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Box>
								<Typography
									style={{ color: TEXT.strong, marginBottom: 6, fontSize: '0.83rem', textAlign: 'left' }}
								>
									Type
								</Typography>
								<TextField
									variant="outlined"
									size="small"
									select
									fullWidth
									value={causeListType}
									onChange={(e) => setCauseListType(e.target.value)}
									SelectProps={{ native: true }}
								>
									<option value="main">Main Cause List</option>
									<option value="supplementary">Supplementary Cause List</option>
								</TextField>
							</Box>
						</Grid>
					</Grid>
					<Button
						variant="contained"
						color="primary"
						className={classes.submitBtn}
						onClick={handleSubmit}
						disabled={!date || loading}
					>
						{loading ? "Loading..." : "Submit"}
					</Button>
					<FormError message={fetchError} />
				</Box>
			</Box>

			{loading && (
				<Box style={{ padding: "2rem 0" }}>
					<CircularCenteredLoader />
				</Box>
			)}

			{!loading && data && (
				<Box>
					<Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 12px 1rem" }}>
						{fetchedDate ? (
							<Typography style={{ fontWeight: 600, fontSize: "1rem", color: TEXT.strong }}>
								Cause List — {fetchedDate}
							</Typography>
						) : <span />}
						<Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
							<Button
								variant="outlined"
								onClick={handleDownload}
								disabled={downloading}
								endIcon={<PrintOutlined />}
								style={{ borderRadius: "0.5rem", padding: "0.4rem 1.25rem" }}
							>
								{downloading ? "Printing..." : "Print"}
							</Button>
							{downloadError && (
								<Typography style={{ color: TEXT.error, fontSize: "0.8125rem" }}>
									{downloadError}
								</Typography>
							)}
						</Box>
					</Box>

					{rooms.length === 0 ? (
						<Typography style={{ padding: "2rem", textAlign: "center" }} color="textSecondary">
							No cases listed for the selected date.
						</Typography>
					) : (
						<Box className={classes.tableWrapper}>
							<TableContainer component={Paper} style={{ overflowX: "auto" }}>
								<Table size="small" aria-label="cause list table" style={{ minWidth: 900, marginTop: 0, border: '1px solid #ddd' }}>
									<TableHead>
										<TableRow className={classes.tableHeadRow}>
											<TableCell style={{ width: 50, whiteSpace: "nowrap" }}>S.No.</TableCell>
											<TableCell style={{ minWidth: 140, whiteSpace: "nowrap" }}>Case No.</TableCell>
											<TableCell style={{ minWidth: 200 }}>Case Title</TableCell>
											<TableCell style={{ minWidth: 260 }}>Counsel(s)</TableCell>
											<TableCell style={{ minWidth: 160 }}>Arbitrator(s)</TableCell>
											<TableCell style={{ minWidth: 120 }}>Stage</TableCell>
											<TableCell style={{ minWidth: 120 }}>Payment</TableCell>
											<TableCell style={{ minWidth: 110, whiteSpace: "nowrap" }}>Time</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{rooms.map((room) => {
											const cases = data[room] || [];
											return (
												<React.Fragment key={room}>
													{/* Room header row */}
													<TableRow>
														<TableCell colSpan={8} className={classes.roomHeader}>
															{room}
														</TableCell>
													</TableRow>

													{cases.length === 0 ? (
														<TableRow>
															<TableCell colSpan={8} align="center" style={{ padding: "16px 0" }}>
																<Typography color="textSecondary">No cases for this room.</Typography>
															</TableCell>
														</TableRow>
													) : (
														cases.map((item, idx) => {
															globalCounter += 1;

															const slots = Array.isArray(item.slots) ? item.slots : [];

															// Arbitrators
															const arbitratorNames = item.arbitrator_names || [];

															return (
																<TableRow key={`${item.id}-${idx}`} hover>
																	{/* S.No. */}
																	<TableCell className={classes.tableBodyCell}>
																		{globalCounter}
																	</TableCell>

																	{/* Case No. */}
																	<TableCell className={classes.tableBodyCell} style={{ whiteSpace: "nowrap" }}>
																		{item.tiac_custom_dispute_id || "—"}
																	</TableCell>

																	{/* Case Title */}
																	<TableCell className={classes.tableBodyCell}>
																		{item.case_name || "—"}
																	</TableCell>

																	{/* Counsels */}
																	<TableCell style={{ padding: "10px", verticalAlign: "top" }}>
																		<CounselGroup
																			label="Claimant Counsel(s)"
																			counsels={item.claimant_counsels}
																			classes={classes}
																		/>
																		<CounselGroup
																			label="Respondent Counsel(s)"
																			counsels={item.respondent_counsels}
																			classes={classes}
																		/>
																	</TableCell>

																	{/* Arbitrator */}
																	<TableCell className={classes.tableBodyCell}>
																		{arbitratorNames.length === 0 ? (
																			<Typography className={classes.arbitratorLine}>—</Typography>
																		) : (
																			arbitratorNames.map((name, i) => (
																				<Typography key={i} className={classes.arbitratorLine}>
																					{name} {((arbitratorNames?.length > 1) && (i === 0)) ? '(Presiding)' : ''}
																				</Typography>
																			))
																		)}
																	</TableCell>

																	{/* Stage */}
																	<TableCell className={classes.tableBodyCell}>
																		{item.stage || "—"}
																	</TableCell>

																	{/* Payment */}
																	<TableCell className={classes.tableBodyCell}>
																		{item.financial_status || "—"}
																	</TableCell>

																	{/* Time */}
																	<TableCell className={classes.tableBodyCell} style={{ whiteSpace: "nowrap" }}>
																		{slots.length === 0 ? (
																			"—"
																		) : (
																			slots.map((slot, slotIndex) => {
																				const fromTime = slot?.from_time;
																				const toTime = slot?.to_time;
																				const timeStr = fromTime && toTime
																					? `${fromTime} – ${toTime}`
																					: fromTime || "—";

																				return (
																					<Typography key={slot.slot_id || slotIndex} className={classes.tableBodyCell}>
																						{timeStr}
																					</Typography>
																				);
																			})
																		)}
																	</TableCell>
																</TableRow>
															);
														})
													)}
												</React.Fragment>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};

export default CauseList;
